import React, { useState, useRef, useEffect } from 'react';
import { Form, Row, Col } from 'react-bootstrap';
import axios from '../../services/axios';

const formatKey = (key) =>
    key.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());

const isPrimitive = (value) =>
    typeof value === 'string' || typeof value === 'number';

const formatOriginalValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) return value.join(', ');
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const EQUIPE_EDITABLE_FIELDS = ['civilite', 'prenom', 'nom', 'email', 'telephone_mobile', 'telephone_fixe'];

const EQUIPE_FIELD_LABEL = {
    civilite: 'Civilité',
    prenom: 'Prénom',
    nom: 'Nom',
    email: 'Email',
    telephone_mobile: 'Tél. mobile',
    telephone_fixe: 'Tél. fixe',
};

const OperationInscriptionEditForm = ({ inscription, updateUrl, csrfToken, availableTagOperations, onSaved, onSubmitReady, setIsSending }) => {
    const { donneesOperation } = inscription;
    const equipe = donneesOperation?.equipe ?? [];

    const [editedFields, setEditedFields] = useState(() => {
        const initial = {};
        if (donneesOperation && typeof donneesOperation === 'object') {
            Object.entries(donneesOperation).forEach(([key, value]) => {
                if (isPrimitive(value)) {
                    initial[key] = inscription.operatorData?.donneesOperation?.[key] ?? null;
                }
            });
        }
        return initial;
    });

    const [editedEquipe, setEditedEquipe] = useState(() => {
        const initial = {};
        equipe.forEach(membre => {
            const opMembre = inscription.operatorData?.donneesOperation?.equipe?.find(m => m.id === membre.id) ?? {};
            initial[membre.id] = {};
            EQUIPE_EDITABLE_FIELDS.forEach(field => {
                initial[membre.id][field] = opMembre[field] ?? null;
            });
        });
        return initial;
    });

    const [comment, setComment] = useState(inscription.comment ?? '');
    const [tagOperation, setTagOperation] = useState(inscription.tagOperation ?? null);

    const handleEquipeChange = (membreId, field, value) => {
        setEditedEquipe(prev => ({
            ...prev,
            [membreId]: { ...prev[membreId], [field]: value },
        }));
    };

    const handleSubmit = () => {
        setIsSending(true);

        const donneesOperationCorrected = {};
        Object.entries(editedFields).forEach(([key, value]) => {
            if (value !== null) donneesOperationCorrected[key] = value;
        });

        const equipeData = equipe
            .map(membre => {
                const edited = editedEquipe[membre.id] ?? {};
                const fields = {};
                EQUIPE_EDITABLE_FIELDS.forEach(field => {
                    if (edited[field] !== null) fields[field] = edited[field];
                });
                if (Object.keys(fields).length === 0) return null;
                return { id: membre.id, ...fields };
            })
            .filter(Boolean);

        if (equipeData.length > 0) {
            donneesOperationCorrected.equipe = equipeData;
        }

        axios.post(updateUrl, {
            csrfToken,
            comment: comment || null,
            operatorData: { donneesOperation: donneesOperationCorrected },
            tagOperation: tagOperation ?? null,
        })
            .then(data => onSaved(data))
            .finally(() => setIsSending(false));
    };

    const handleSubmitRef = useRef();
    handleSubmitRef.current = handleSubmit;

    // eslint-disable-next-line react-hooks/exhaustive-deps -- register once with a stable wrapper that always calls the latest handleSubmit
    useEffect(() => { onSubmitReady?.(() => handleSubmitRef.current()); }, []);

    const primitiveEntries = donneesOperation
        ? Object.entries(donneesOperation).filter(([, value]) => isPrimitive(value))
        : [];

    const otherComplexEntries = donneesOperation
        ? Object.entries(donneesOperation).filter(([key, value]) => !isPrimitive(value) && key !== 'equipe')
        : [];

    return (
        <div>
            {primitiveEntries.length > 0 && (
                <section className="mb-4">
                    <h5>Données de l'opération</h5>
                    {primitiveEntries.map(([key, originalValue]) => (
                        <Form.Group as={Row} key={key} className="mb-2 align-items-center">
                            <Form.Label column sm={3}>{formatKey(key)}</Form.Label>
                            <Col sm={4} className="text-muted small d-flex align-items-center">
                                {String(originalValue)}
                            </Col>
                            <Col sm={5}>
                                <Form.Control
                                    as={typeof originalValue === 'string' ? 'textarea' : undefined}
                                    rows={typeof originalValue === 'string' ? 2 : undefined}
                                    value={editedFields[key] ?? ''}
                                    onChange={e => setEditedFields(prev => ({ ...prev, [key]: e.target.value || null }))}
                                    placeholder="Valeur corrigée…"
                                />
                            </Col>
                        </Form.Group>
                    ))}
                </section>
            )}

            {equipe.length > 0 && (
                <section className="mb-4">
                    <h5>Équipe</h5>
                    {equipe.map(membre => (
                        <div key={membre.id} className="border rounded p-3 mb-2">
                            <p className="fw-bold mb-2">
                                {membre.civilite} {membre.prenom} {membre.nom}
                            </p>
                            {EQUIPE_EDITABLE_FIELDS.map(field => (
                                <Form.Group as={Row} key={field} className="mb-2 align-items-center">
                                    <Form.Label column sm={3}>{EQUIPE_FIELD_LABEL[field]}</Form.Label>
                                    <Col sm={4} className="text-muted small d-flex align-items-center">
                                        {membre[field] || '—'}
                                    </Col>
                                    <Col sm={5}>
                                        <Form.Control
                                            value={editedEquipe[membre.id]?.[field] ?? ''}
                                            onChange={e => handleEquipeChange(membre.id, field, e.target.value || null)}
                                            placeholder="Valeur corrigée…"
                                        />
                                    </Col>
                                </Form.Group>
                            ))}
                            {membre.roles?.length > 0 && (
                                <Form.Group as={Row} className="mb-0">
                                    <Form.Label column sm={3}>Rôles</Form.Label>
                                    <Col sm={9} className="text-muted small d-flex align-items-center">
                                        {membre.roles.map(r => r === 'autre' && membre.role_autre_precis
                                            ? `Autre : ${membre.role_autre_precis}`
                                            : r
                                        ).join(', ')}
                                    </Col>
                                </Form.Group>
                            )}
                        </div>
                    ))}
                </section>
            )}

            {otherComplexEntries.length > 0 && (
                <section className="mb-4">
                    {otherComplexEntries.map(([key, value]) => (
                        <Form.Group as={Row} key={key} className="mb-2">
                            <Form.Label column sm={3}>{formatKey(key)}</Form.Label>
                            <Col sm={9} className="text-muted small d-flex align-items-center">
                                {formatOriginalValue(value)}
                            </Col>
                        </Form.Group>
                    ))}
                </section>
            )}

            {availableTagOperations?.length > 0 && (
                <section className="mb-4">
                    <h5>Tag opération</h5>
                    <Form.Select
                        value={tagOperation ?? ''}
                        onChange={e => setTagOperation(e.target.value || null)}
                    >
                        <option value="">— Aucun —</option>
                        {availableTagOperations.map(tag => (
                            <option key={tag.uniqueId} value={tag.uniqueId}>{tag.name}</option>
                        ))}
                    </Form.Select>
                </section>
            )}

            <section className="mb-4">
                <h5>Commentaire</h5>
                <Form.Control
                    as="textarea"
                    rows={3}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    placeholder="Commentaire de l'opérateur…"
                />
            </section>

        </div>
    );
};

export default OperationInscriptionEditForm;
